// iOS 三方渠道（Xsolla / EnjoyPay）配置的规范化、当前渠道推导与互斥合并
// 页面侧只持有两份草稿 + 一个总开关 + 一个当前渠道，下发时才组装成 channelPrices

export const THIRD_PARTY_CHANNELS: goodsSetting.ThirdPartyPayChannel[] = ['xsolla_pay', 'enjoy_pay']

export const THIRD_PARTY_CHANNEL_OPTIONS: { label: string; value: goodsSetting.ThirdPartyPayChannel }[] = [
  { label: 'Xsolla', value: 'xsolla_pay' },
  { label: 'EnjoyPay', value: 'enjoy_pay' },
]

// 默认渠道：iOS 新建/未开启时选中 Xsolla
export const DEFAULT_THIRD_PARTY_CHANNEL: goodsSetting.ThirdPartyPayChannel = 'xsolla_pay'

// 各三方渠道支持的商品类型：Xsolla 支持消耗型(20)/非消耗型(10)，EnjoyPay 只支持消耗型(20)
const SUPPORTED_PROD_TYPES: Record<goodsSetting.ThirdPartyPayChannel, string[]> = {
  xsolla_pay: ['10', '20'],
  enjoy_pay: ['20'],
}

export const isProdTypeSupported = (payChnl: goodsSetting.ThirdPartyPayChannel, prodType?: string | number): boolean =>
  !!SUPPORTED_PROD_TYPES[payChnl]?.includes(String(prodType ?? ''))

// 该商品类型下是否还有可用的三方渠道，没有则整个三方支付页签都不展示
export const hasThirdPartySupport = (prodType?: string | number): boolean =>
  THIRD_PARTY_CHANNELS.some((key) => isProdTypeSupported(key, prodType))

export const createThirdPartyDraft = (): goodsSetting.ThirdPartyChannelDraft => ({
  prodPrice: '',
  promotionDesc: '',
})

export const normalizeThirdPartyDraft = (
  info?: goodsSetting.ThirdPartyPayInfo | null,
): goodsSetting.ThirdPartyChannelDraft => ({
  prodPrice: info?.prodPrice ?? '',
  promotionDesc: info?.promotionDesc ?? '',
})

// 从两个渠道的 enableThirdPay 推导总开关和当前渠道，不依赖额外持久化字段。
// 历史数据可能出现双开，此时按 Xsolla 优先取唯一渠道，避免页面出现两个同时生效的渠道。
export const resolveThirdPartyChannel = (
  priceInfo?: goodsSetting.ChannelPriceInfo | null,
): { enabled: boolean; channel: goodsSetting.ThirdPartyPayChannel | '' } => {
  const active = THIRD_PARTY_CHANNELS.find((key) => priceInfo?.[key]?.enableThirdPay === true)
  return active ? { enabled: true, channel: active } : { enabled: false, channel: '' }
}

type MergeState = {
  enabled: boolean
  channel: goodsSetting.ThirdPartyPayChannel | ''
  // 页面只维护一份价格草稿，渠道决定它写到哪个 key 下
  draft: goodsSetting.ThirdPartyChannelDraft
  discount?: string
}

// 合并出下发用的 channelPrices：
// - 草稿只写入当前所选渠道，enableThirdPay 由总开关决定
// - 未选中的渠道只把开关置 false，历史价格和描述原样保留
// - 原 channelPrices 上的其他键（含 extInfo 与未知键）不丢
// - 没有任何渠道内容时返回 undefined，避免给新建商品下发空结构
export const mergeThirdPartyChannelPrices = (
  channelPrices: goodsSetting.ChannelPrices | undefined,
  state: MergeState,
): goodsSetting.ChannelPrices | undefined => {
  const priceInfo: goodsSetting.ChannelPriceInfo = { ...(channelPrices?.priceInfo || {}) }
  let hasChannel = false

  THIRD_PARTY_CHANNELS.forEach((key) => {
    const saved = priceInfo[key]
    const isActive = key === state.channel

    if (!isActive) {
      if (!saved) return
      hasChannel = true
      priceInfo[key] = { ...saved, enableThirdPay: false }
      return
    }

    // 未开启且历史上也没配过（新建关着三方支付保存）：整个 channelPrices 都不下发
    if (!state.enabled && !saved) return
    hasChannel = true
    priceInfo[key] = {
      ...(saved || {}),
      prodPriceCurrency: 'USD',
      enableThirdPay: state.enabled,
      // 没填的字段下发 null，不下发空串
      prodPrice: state.draft.prodPrice || null,
      promotionDesc: state.draft.promotionDesc || null,
      promotionDiscount: state.discount || saved?.promotionDiscount || null,
    }
  })

  if (!hasChannel) return undefined
  return { ...(channelPrices || {}), priceInfo }
}
